import type {
  Flights,
  MulticityFlights,
  Prediction,
  ShopMulticitySummarySuccess,
  ShopSummaryResponseV4,
  TripDetailsV2,
} from "@b2bportal/air-shopping-api";
import {
  CallState,
  FlightShopStep,
  type MulticityFlightShopStep,
  type ISelectedMulticityTrip,
  type ISelectedTrip,
  SortOptionEnum,
  MulticityFlightShopStepKeys,
} from "@b2bportal/core-types";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  fetchFlights,
  fetchMulticityFlights,
  fetchTripDetails,
} from "./thunks";
import type {
  ISetChoseReturningSliceParams,
  ISetChosenOutgoingSliceParams,
  ITripDetailsByTripId,
} from "./types";

export interface FlightsShopState {
  flights?: Flights;
  multicityFlights?: MulticityFlights;
  flightsLoading?: boolean;
  flightsCallState: CallState;
  flightsError: boolean;
  tripDetailsById: ITripDetailsByTripId;
  selectedTrip: ISelectedTrip;
  sortOption: SortOptionEnum;
  shopStep: FlightShopStep;
  multicityShopStep: MulticityFlightShopStep;
  prediction?: Prediction;
  selectedMulticityTrips: ISelectedMulticityTrip[];
}

export const initialShopState: FlightsShopState = {
  flights: undefined,
  flightsCallState: CallState.NotCalled,
  flightsError: false,
  selectedTrip: {
    tripId: "",
    outgoingSliceId: "",
    outgoingFareId: "",
  },
  prediction: undefined,
  tripDetailsById: {},
  sortOption: SortOptionEnum.FareScore,
  shopStep: FlightShopStep.departure,
  multicityShopStep: MulticityFlightShopStepKeys[0],
  selectedMulticityTrips: [
    { tripId: null, outgoingSliceId: null, outgoingFareId: null },
    { tripId: null, outgoingSliceId: null, outgoingFareId: null },
  ],
};

const setFetchFlightsPending = (state: FlightsShopState) => {
  state.flightsCallState = CallState.InProcess;
  state.flightsLoading = true;
};

const setFetchFlightsSuccess = (
  state: FlightsShopState,
  action: PayloadAction<ShopSummaryResponseV4>
) => {
  state.flightsCallState = CallState.Success;
  state.flightsError = false;
  state.flights = action.payload.flights;
  // Clear stale multi-city data to prevent useSelectDepartureFlight
  // from taking the wrong code path when switching from multi-city to one-way
  state.multicityFlights = undefined;
  state.prediction = action.payload.prediction as Prediction;
  state.flightsLoading = false;
};

const setFetchFlightsRejected = (state: FlightsShopState) => {
  state.flightsCallState = CallState.Failed;
  state.flightsError = true;
  state.flightsLoading = false;
};

const setFetchTripDetails = (
  state: FlightsShopState,
  action: PayloadAction<TripDetailsV2>
) => {
  state.tripDetailsById = {
    ...state.tripDetailsById,
    [action.payload.id]: action.payload,
  };
};

const setFetchMulticityFlightsSuccess = (
  state: FlightsShopState,
  action: PayloadAction<ShopMulticitySummarySuccess>
) => {
  state.flightsCallState = CallState.Success;
  state.flightsError = false;
  state.multicityFlights = action.payload.flights;
  // Clear stale one-way/round-trip data to prevent useSelectDepartureFlight
  // from taking the wrong code path when switching from one-way to multi-city
  state.flights = undefined;
  state.flightsLoading = false;
};

export const flightShopReducers = {
  setChosenOutgoingSlice: (
    state,
    action: PayloadAction<ISetChosenOutgoingSliceParams>
  ) => {
    state.selectedTrip = action.payload;
    state.shopStep = FlightShopStep.return;
  },
  setChosenReturnSlice: (
    state,
    action: PayloadAction<ISetChoseReturningSliceParams>
  ) => {
    state.selectedTrip = { ...state.selectedTrip, ...action.payload };
    state.shopStep = FlightShopStep.review;
  },
  setSortOption: (state, action: PayloadAction<SortOptionEnum>) => {
    state.sortOption = action.payload;
  },
  setFetchFlightsPending,
  setFetchFlightsRejected,
  setFetchFlightsSuccess,
  setFetchTripDetails,
  setSelectedMulticityTrip: (
    state: FlightsShopState,
    action: PayloadAction<{
      selectedTrip: ISelectedMulticityTrip;
      index: number;
    }>
  ) => {
    const { selectedTrip, index } = action.payload;

    // remove the flights that are selected after this index and force user to choose again
    state.selectedMulticityTrips.splice(index);
    state.selectedMulticityTrips[index] = selectedTrip;
    state.selectedTrip = selectedTrip;

    if (index === 0) {
      state.selectedMulticityTrips[1] = {
        tripId: null,
        outgoingSliceId: null,
        outgoingFareId: null,
      };
    }
  },
  setMulticityShopNextStep: (state: FlightsShopState) => {
    state.multicityShopStep = state.multicityShopStep + 1;
  },
  setMulticityShopStep: (
    state: FlightsShopState,
    action: PayloadAction<number>
  ) => {
    const newStep = MulticityFlightShopStepKeys[action.payload];
    if (state.multicityShopStep !== newStep) {
      state.multicityShopStep = newStep;
    }
  },
};

export const flightShopSlice = createSlice({
  name: "flightShop",
  initialState: initialShopState,
  reducers: flightShopReducers,
  extraReducers: (builder) => {
    builder
      // Handle fetchFlights
      .addCase(fetchFlights.pending, setFetchFlightsPending)
      .addCase(fetchFlights.fulfilled, setFetchFlightsSuccess)
      .addCase(fetchFlights.rejected, setFetchFlightsRejected)
      // Handle fetchTripDetails
      .addCase(fetchTripDetails.fulfilled, setFetchTripDetails)
      // Handle fetchMulticityFlights
      .addCase(fetchMulticityFlights.pending, setFetchFlightsPending)
      .addCase(fetchMulticityFlights.fulfilled, setFetchMulticityFlightsSuccess)
      .addCase(fetchMulticityFlights.rejected, setFetchFlightsRejected);
  },
});
export const {
  setChosenOutgoingSlice,
  setChosenReturnSlice,
  setSortOption,
  setSelectedMulticityTrip,
} = flightShopSlice.actions;

export const FlightShopActions = flightShopSlice.actions;

export default flightShopSlice.reducer;
